import type { LevelCrossing, RailKind, RailLine, Road, World } from './types.js';
import { Heightfield } from './heightfield.js';
import { DeckIndex, type DeckProfile } from './decks.js';
import { hashString, hashUnit } from './rng.js';
import { ROAD_SPECS } from './osm/classify.js';

/**
 * The railway as it runs: where the rails are, which trains are on them and where, and what
 * the level crossings are doing about it.
 *
 * All of it is a pure function of the world and a clock. There is no train state anywhere —
 * not on the server, not in the browser — and nothing about trains goes over the wire. Every
 * client works out the same schedule from the same world, reads the server's clock through
 * `ClockSync`, and so sees the same train at the same crossing; the server works out the
 * same answer from `Date.now()` when an AI opponent has to be stopped by one. See DECISIONS.md
 * §95 for why that, rather than the server announcing trains.
 */

// ---------------------------------------------------------------------------
// the track's cross-section, shared by the mesh and the trains that ride on it
// ---------------------------------------------------------------------------

/** Top of the ballast and the sleepers, over the formation the track is laid on. */
export const BALLAST_TOP = 0.3;
/** Rail head over the sleeper tops. */
export const RAIL_HEAD = 0.16;
/** Rail head over the formation: where a wheel is. */
export const RAIL_TOP = BALLAST_TOP + RAIL_HEAD;
/** A rail set in a street stands this proud of the tarmac, which keeps it out of the road's depth. */
export const FLUSH_LIFT = 0.035;
/** Standard gauge, and the 750 mm of the narrow-gauge lines Poland still runs. */
export const GAUGE = 1.435;
export const NARROW_GAUGE = 0.75;

/** How far beyond a carriageway's edge a track climbs from flush back up onto its ballast. */
const FLUSH_RAMP = 3;
/** Two surfaces further apart than this in height are not the same level, whatever the plan says. */
const SAME_LEVEL = 1.5;
/** Station spacing on terrain, on decks, and around a change from ballast to street. */
const DRAPE_STEP = 8;
const DECK_DRAPE_STEP = 4;
const FLUSH_STEP = 1.5;
/** Fine enough that no street is stepped over without a sample landing within reach of it. */
const STREET_SEARCH_STEP = 6;

// ---------------------------------------------------------------------------
// draped lines
// ---------------------------------------------------------------------------

/** One railway way, densified and laid on whatever is under it. */
export interface DrapedLine {
  line: RailLine;
  /** x,z pairs; the way's own vertices are all kept. */
  pts: number[];
  /** OSM node per point, -1 for a clipped end, -2 for a point added here. */
  nodes: number[];
  /** Height of what the track is laid on: the ground, a deck, or the street. */
  base: Float64Array;
  /** 0 on its own ballast, 1 flush with a street — a crossing, or a tram in the road. */
  flush: Float64Array;
  /** Rail-head height, which is what a wheel rides on. */
  top: Float64Array;
  /** In a tunnel: nothing drawn, and a train there is not drawn either. */
  hidden: boolean;
}

/** What the railway is laid on, and the streets it may be laid in. */
export interface RailGround {
  terrain: Heightfield;
  decks: DeckIndex;
}

export function railGroundFor(world: World): RailGround {
  const terrain = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();
  return { terrain, decks: DeckIndex.build(world, terrain) };
}

/**
 * Every line in the world, draped.
 *
 * A line on its own formation follows the ground, or its deck where `DeckIndex` gave it one,
 * exactly as a road does. Where it runs into a carriageway at the same level — a level
 * crossing, or a tram in the street — it comes down to the street's surface instead, so the
 * rails are flush with the tarmac a car drives over, and climbs back onto its ballast over
 * `FLUSH_RAMP` beyond the kerb.
 */
export function drapeRailLines(world: World, ground: RailGround): DrapedLine[] {
  const lines = world.rail?.lines ?? [];
  if (!lines.length) return [];
  const { terrain, decks } = ground;
  const profiles = decks.profileMap();
  const lifts = decks.liftMap();
  const streets = new StreetGrid(world.roads, (road, x, z) => {
    const deck = profiles.get(road.id);
    const at = deck ? deck(x, z) : terrain.heightAt(x, z) + (lifts.get(road.id)?.(x, z) ?? 0);
    return at + road.y;
  });

  const out: DrapedLine[] = [];
  for (const line of lines) {
    const deck: DeckProfile | null = line.embedded ? null : profiles.get(line.id) ?? null;
    const lift: DeckProfile | null = line.embedded || deck ? null : lifts.get(line.id) ?? null;
    const own = (x: number, z: number) => (deck ? deck(x, z) : terrain.heightAt(x, z) + (lift ? lift(x, z) : 0));
    const level = line.embedded ? Infinity : SAME_LEVEL;
    const near = (x: number, z: number) =>
      line.deck === 'tunnel' ? null : streets.nearest(x, z, line.embedded ? null : own(x, z), level);

    const step = line.deck === 'bridge' || lift ? DECK_DRAPE_STEP : terrain.isFlat ? Infinity : DRAPE_STEP;
    const pts: number[] = [];
    const nodes: number[] = [];
    for (let i = 0; i < line.pts.length / 2; i++) {
      const x = line.pts[i * 2];
      const z = line.pts[i * 2 + 1];
      if (i > 0) {
        const ax = line.pts[i * 2 - 2];
        const az = line.pts[i * 2 - 1];
        const length = Math.hypot(x - ax, z - az);
        // Coarse first, then fine only where the track is near a street, so the ramp down to
        // one is a ramp and a long straight across open ground is still two points.
        const meets = !line.embedded && line.deck !== 'tunnel' && (near(ax, az) || near(x, z) || crossesStreet(streets, ax, az, x, z));
        const coarse = meets ? Math.min(step, STREET_SEARCH_STEP) : step;
        const n = Number.isFinite(coarse) ? Math.max(1, Math.ceil(length / coarse)) : 1;
        let px = ax;
        let pz = az;
        let wasNear = meets && !!near(ax, az);
        let since = 0;
        for (let k = 1; k <= n; k++) {
          const qx = ax + ((x - ax) * k) / n;
          const qz = az + ((z - az) * k) / n;
          const isNear = meets && !!near(qx, qz);
          const gap = Math.hypot(qx - px, qz - pz);
          since += gap;
          if (wasNear || isNear) {
            const m = Math.max(1, Math.ceil(gap / FLUSH_STEP));
            for (let j = 1; j < m; j++) {
              pts.push(px + ((qx - px) * j) / m, pz + ((qz - pz) * j) / m);
              nodes.push(-2);
            }
          }
          // Out in the open only as often as the ground needs, and never on a flat world.
          if (k < n && (wasNear || isNear || since >= step - 1e-6)) {
            pts.push(qx, qz);
            nodes.push(-2);
            since = 0;
          }
          px = qx;
          pz = qz;
          wasNear = isNear;
        }
      }
      pts.push(x, z);
      nodes.push(line.nodes[i] ?? -1);
    }

    const n = pts.length / 2;
    const base = new Float64Array(n);
    const flush = new Float64Array(n);
    const top = new Float64Array(n);
    const streetY = new Float64Array(n).fill(NaN);
    // Flush only where the track is actually in a carriageway, and ramping by distance *along
    // the track* from there — so a line running beside a street a metre off its kerb stays on
    // its ballast, and only the line crossing it comes down to meet it.
    const along = new Float64Array(n).fill(Infinity);
    for (let i = 0; i < n; i++) {
      const hit = near(pts[i * 2], pts[i * 2 + 1]);
      if (hit) streetY[i] = hit.y;
      if (line.embedded || (hit && hit.distance <= hit.half)) along[i] = 0;
    }
    for (let pass = 0; pass < 2; pass++) {
      for (let k = 1; k < n; k++) {
        const i = pass === 0 ? k : n - 1 - k;
        const j = pass === 0 ? i - 1 : i + 1;
        const gap = Math.hypot(pts[i * 2] - pts[j * 2], pts[i * 2 + 1] - pts[j * 2 + 1]);
        if (along[j] + gap < along[i]) along[i] = along[j] + gap;
      }
    }
    for (let i = 0; i < n; i++) {
      const x = pts[i * 2];
      const z = pts[i * 2 + 1];
      const formation = own(x, z);
      const f = along[i] >= FLUSH_RAMP || Number.isNaN(streetY[i]) ? (line.embedded ? 1 : 0) : 1 - smooth(along[i] / FLUSH_RAMP);
      const sy = Number.isNaN(streetY[i]) ? formation : streetY[i];
      flush[i] = f;
      base[i] = lerp(formation, sy, f);
      top[i] = lerp(formation + RAIL_TOP, sy + FLUSH_LIFT, f);
    }
    out.push({ line, pts, nodes, base, flush, top, hidden: line.deck === 'tunnel' });
  }
  return out;
}

function crossesStreet(streets: StreetGrid, ax: number, az: number, bx: number, bz: number): boolean {
  const length = Math.hypot(bx - ax, bz - az);
  const n = Math.ceil(length / 4);
  for (let k = 1; k < n; k++) {
    if (streets.nearest(ax + ((bx - ax) * k) / n, az + ((bz - az) * k) / n, null, Infinity, FLUSH_RAMP + 2)) return true;
  }
  return false;
}

/**
 * Drivable carriageways on a grid, asked "which street am I in, or beside, and how high is
 * its surface here".
 */
class StreetGrid {
  private readonly grid = new Map<number, number[]>();
  private readonly segs: { road: Road; ax: number; az: number; bx: number; bz: number; half: number }[] = [];
  private static readonly CELL = 32;
  private static readonly PAD = FLUSH_RAMP + 2;

  constructor(
    roads: Road[],
    private readonly surface: (road: Road, x: number, z: number) => number,
  ) {
    const cell = StreetGrid.CELL;
    for (const road of roads) {
      if (!ROAD_SPECS[road.cls].drivable || road.deck === 'tunnel') continue;
      const half = road.width / 2;
      const reach = half + StreetGrid.PAD;
      for (let i = 2; i < road.pts.length; i += 2) {
        const index = this.segs.length;
        const s = { road, ax: road.pts[i - 2], az: road.pts[i - 1], bx: road.pts[i], bz: road.pts[i + 1], half };
        this.segs.push(s);
        for (let cx = Math.floor((Math.min(s.ax, s.bx) - reach) / cell); cx <= Math.floor((Math.max(s.ax, s.bx) + reach) / cell); cx++) {
          for (let cz = Math.floor((Math.min(s.az, s.bz) - reach) / cell); cz <= Math.floor((Math.max(s.az, s.bz) + reach) / cell); cz++) {
            const key = cx * 100003 + cz;
            const bucket = this.grid.get(key);
            if (bucket) bucket.push(index);
            else this.grid.set(key, [index]);
          }
        }
      }
    }
  }

  /**
   * The street whose kerb is nearest a point (inside it counts as the nearest of all), at
   * about the height `y` if one is given, within `pad` of its edge.
   */
  nearest(
    x: number,
    z: number,
    y: number | null,
    level: number,
    pad = StreetGrid.PAD,
  ): { distance: number; half: number; y: number } | null {
    const bucket = this.grid.get(Math.floor(x / StreetGrid.CELL) * 100003 + Math.floor(z / StreetGrid.CELL));
    if (!bucket) return null;
    let best: { distance: number; half: number; y: number } | null = null;
    let bestOutside = Infinity;
    for (const index of bucket) {
      const s = this.segs[index];
      const vx = s.bx - s.ax;
      const vz = s.bz - s.az;
      const len2 = vx * vx + vz * vz;
      let t = len2 > 1e-9 ? ((x - s.ax) * vx + (z - s.az) * vz) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const distance = Math.hypot(x - s.ax - vx * t, z - s.az - vz * t);
      const outside = distance - s.half;
      if (outside > pad || outside >= bestOutside) continue;
      const sy = this.surface(s.road, x, z);
      if (y !== null && Math.abs(sy - y) > level) continue;
      bestOutside = outside;
      best = { distance, half: s.half, y: sy };
    }
    return best;
  }
}

// ---------------------------------------------------------------------------
// rolling stock
// ---------------------------------------------------------------------------

/**
 * What a vehicle is. The lengths are the real ones, near enough, because they decide how long
 * a crossing stays shut and whether a train fits a platform.
 */
export type RailCarType =
  | 'loco-electric'
  | 'loco-diesel'
  | 'emu-cab'
  | 'emu-mid'
  | 'dmu'
  | 'coach'
  | 'wagon-box'
  | 'wagon-tank'
  | 'wagon-hopper'
  | 'wagon-flat'
  | 'tram-cab'
  | 'tram-mid'
  | 'narrow-loco'
  | 'narrow-coach';

export const RAIL_CAR_TYPES: readonly RailCarType[] = [
  'loco-electric',
  'loco-diesel',
  'emu-cab',
  'emu-mid',
  'dmu',
  'coach',
  'wagon-box',
  'wagon-tank',
  'wagon-hopper',
  'wagon-flat',
  'tram-cab',
  'tram-mid',
  'narrow-loco',
  'narrow-coach',
];

export interface RailCarSpec {
  length: number;
  width: number;
  height: number;
  /** Has windows that are lit at night. */
  windows: boolean;
}

export const RAIL_CAR_SPECS: Record<RailCarType, RailCarSpec> = {
  'loco-electric': { length: 16.2, width: 3.0, height: 4.3, windows: false },
  'loco-diesel': { length: 14.2, width: 3.0, height: 4.1, windows: false },
  'emu-cab': { length: 21, width: 2.9, height: 4.1, windows: true },
  'emu-mid': { length: 20, width: 2.9, height: 4.1, windows: true },
  dmu: { length: 22, width: 2.9, height: 4.0, windows: true },
  coach: { length: 24.5, width: 2.9, height: 4.05, windows: true },
  'wagon-box': { length: 15, width: 2.9, height: 4.0, windows: false },
  'wagon-tank': { length: 13, width: 2.9, height: 3.9, windows: false },
  'wagon-hopper': { length: 13.5, width: 3.0, height: 3.6, windows: false },
  'wagon-flat': { length: 19.6, width: 2.9, height: 3.6, windows: false },
  'tram-cab': { length: 11, width: 2.4, height: 3.4, windows: true },
  'tram-mid': { length: 9, width: 2.4, height: 3.4, windows: true },
  'narrow-loco': { length: 9, width: 2.3, height: 3.2, windows: false },
  'narrow-coach': { length: 11, width: 2.3, height: 3.1, windows: true },
};

/** Coupled with buffers and couplings between them, or articulated with a bellows. */
const COUPLED_GAP = 0.9;
const ARTICULATED_GAP = 0.35;

export interface RailCar {
  type: RailCarType;
  /** Turned round: an EMU's rear cab car faces backwards. */
  flip: boolean;
  /** Which paint scheme, an index the renderer understands per type. */
  livery: number;
  /** Distance from the train's front to this car's front. */
  offset: number;
}

type Service = 'regional' | 'intercity' | 'freight' | 'tram' | 'narrow';

interface Consist {
  service: Service;
  cars: RailCar[];
  length: number;
  /** Cruising speed, m/s. */
  speed: number;
  /** Calls at stations. */
  stops: boolean;
}

/** Liveries per type, for `RailCar.livery` — the renderer draws one of each. */
export const LIVERY_COUNT: Record<RailCarType, number> = {
  'loco-electric': 3,
  'loco-diesel': 2,
  'emu-cab': 3,
  'emu-mid': 3,
  dmu: 2,
  coach: 2,
  'wagon-box': 3,
  'wagon-tank': 3,
  'wagon-hopper': 3,
  'wagon-flat': 2,
  'tram-cab': 3,
  'tram-mid': 3,
  'narrow-loco': 1,
  'narrow-coach': 1,
};

function consistFor(family: Family, electrified: boolean, roll: (k: number) => number): Consist {
  const types: { type: RailCarType; flip?: boolean; livery: number }[] = [];
  let service: Service;
  let articulated = false;
  if (family === 'tram') {
    service = 'tram';
    articulated = true;
    const livery = Math.floor(roll(1) * LIVERY_COUNT['tram-cab']);
    const mids = roll(2) < 0.35 ? 1 : roll(2) < 0.8 ? 2 : 3;
    types.push({ type: 'tram-cab', livery });
    for (let i = 0; i < mids; i++) types.push({ type: 'tram-mid', livery });
    types.push({ type: 'tram-cab', flip: true, livery });
  } else if (family === 'narrow') {
    service = 'narrow';
    types.push({ type: 'narrow-loco', livery: 0 });
    const n = 2 + Math.floor(roll(2) * 3);
    for (let i = 0; i < n; i++) types.push({ type: 'narrow-coach', livery: 0 });
  } else {
    const r = roll(0);
    service = r < 0.45 ? 'regional' : r < 0.68 ? 'intercity' : 'freight';
    if (service === 'regional') {
      if (electrified) {
        articulated = true;
        const livery = Math.floor(roll(1) * LIVERY_COUNT['emu-cab']);
        const mids = 1 + Math.floor(roll(2) * 3);
        types.push({ type: 'emu-cab', livery });
        for (let i = 0; i < mids; i++) types.push({ type: 'emu-mid', livery });
        types.push({ type: 'emu-cab', flip: true, livery });
      } else {
        const livery = Math.floor(roll(1) * LIVERY_COUNT.dmu);
        types.push({ type: 'dmu', livery });
        if (roll(2) < 0.5) types.push({ type: 'dmu', flip: true, livery });
      }
    } else if (service === 'intercity') {
      const loco: RailCarType = electrified ? 'loco-electric' : 'loco-diesel';
      types.push({ type: loco, livery: Math.floor(roll(1) * LIVERY_COUNT[loco]) });
      const livery = Math.floor(roll(3) * LIVERY_COUNT.coach);
      const n = 4 + Math.floor(roll(2) * 5);
      for (let i = 0; i < n; i++) types.push({ type: 'coach', livery });
    } else {
      const loco: RailCarType = electrified ? 'loco-electric' : 'loco-diesel';
      types.push({ type: loco, livery: Math.floor(roll(1) * LIVERY_COUNT[loco]) });
      // A block train of one kind is the commonest thing on a Polish main line; a mixed
      // rake is the rest.
      const kinds: RailCarType[] = ['wagon-box', 'wagon-tank', 'wagon-hopper', 'wagon-flat'];
      const block = roll(4) < 0.6 ? kinds[Math.floor(roll(5) * kinds.length)] : null;
      const n = 10 + Math.floor(roll(2) * 16);
      for (let i = 0; i < n; i++) {
        const type = block ?? kinds[Math.floor(roll(10 + i) * kinds.length)];
        types.push({ type, livery: Math.floor(roll(40 + i) * LIVERY_COUNT[type]) });
      }
    }
  }

  const cars: RailCar[] = [];
  let offset = 0;
  for (let i = 0; i < types.length; i++) {
    const t = types[i];
    if (i > 0) offset += articulated ? ARTICULATED_GAP : COUPLED_GAP;
    cars.push({ type: t.type, flip: t.flip ?? false, livery: t.livery, offset });
    offset += RAIL_CAR_SPECS[t.type].length;
  }
  const speed =
    service === 'tram' ? 8.5 + roll(6) * 2 : service === 'narrow' ? 7 : service === 'freight' ? 12 + roll(6) * 4 : service === 'intercity' ? 18 + roll(6) * 4 : 15 + roll(6) * 4;
  return { service, cars, length: offset, speed, stops: service !== 'freight' };
}

// ---------------------------------------------------------------------------
// the network a train can run on
// ---------------------------------------------------------------------------

type Family = 'rail' | 'tram' | 'narrow';

function familyOf(kind: RailKind): Family {
  return kind === 'tram' ? 'tram' : kind === 'narrow_gauge' ? 'narrow' : 'rail';
}

/** A piece of one draped line between two places the network branches or ends. */
interface Edge {
  line: number;
  i0: number;
  i1: number;
  v0: number;
  v1: number;
  length: number;
}

interface Vertex {
  edges: number[];
  /** Clipped by the region: a train can come from, or go to, beyond it. */
  portal: boolean;
  x: number;
  z: number;
}

/** The path a scheduled train follows, as one polyline. */
interface RunPath {
  xs: Float64Array;
  zs: Float64Array;
  ys: Float64Array;
  /** Cumulative length at each point. */
  s: Float64Array;
  hidden: Uint8Array;
  /** Which draped line each segment (point i to i+1) is on. */
  lineOf: Int32Array;
  length: number;
  /** Which network edge covers which stretch, for keeping trains apart. */
  edges: { edge: number; s0: number; s1: number }[];
  /** Ends at a buffer stop rather than leaving the region: the train goes in and comes back. */
  terminus: boolean;
  family: Family;
  electrified: boolean;
  key: string;
}

/**
 * Bends sharper than this at a junction are not a way through it. A turnout is a few degrees;
 * the rest is the slop OSM's geometry has right at a switch.
 */
const MAX_DEFLECTION = (40 * Math.PI) / 180;
/** Shorter than this and a run is a shunt across a junction, not a journey. */
const MIN_RUN = 200;
/** A dead end this far from anywhere a train calls is a siding nobody tagged as one. */
const TERMINUS_STOP_REACH = 450;

function buildNetwork(draped: DrapedLine[]): { vertices: Vertex[]; edges: Edge[] } {
  const usable = (d: DrapedLine) => !d.line.siding;
  // A node joins lines of the same family; a tram and a main line crossing on the level is
  // not a junction either of them can take.
  const users = new Map<string, number>();
  draped.forEach((d) => {
    if (!usable(d)) return;
    const fam = familyOf(d.line.kind);
    for (let i = 0; i < d.nodes.length; i++) {
      const node = d.nodes[i];
      if (node < 0) continue;
      const key = `${fam}:${node}`;
      users.set(key, (users.get(key) ?? 0) + 1);
    }
  });

  const vertices: Vertex[] = [];
  const vertexKey = new Map<string, number>();
  const edges: Edge[] = [];
  const vertexAt = (d: DrapedLine, li: number, i: number): number => {
    const node = d.nodes[i];
    const fam = familyOf(d.line.kind);
    const key = node >= 0 ? `${fam}:${node}` : `c${li}:${i}`;
    let v = vertexKey.get(key);
    if (v === undefined) {
      v = vertices.length;
      vertices.push({ edges: [], portal: node === -1, x: d.pts[i * 2], z: d.pts[i * 2 + 1] });
      vertexKey.set(key, v);
    }
    return v;
  };

  draped.forEach((d, li) => {
    if (!usable(d)) return;
    const fam = familyOf(d.line.kind);
    const n = d.nodes.length;
    let start = 0;
    for (let i = 1; i < n; i++) {
      const node = d.nodes[i];
      const split = i === n - 1 || (node >= 0 && (users.get(`${fam}:${node}`) ?? 0) > 1);
      if (!split) continue;
      const v0 = vertexAt(d, li, start);
      const v1 = vertexAt(d, li, i);
      let length = 0;
      for (let k = start + 1; k <= i; k++) {
        length += Math.hypot(d.pts[k * 2] - d.pts[k * 2 - 2], d.pts[k * 2 + 1] - d.pts[k * 2 - 1]);
      }
      const e = edges.length;
      edges.push({ line: li, i0: start, i1: i, v0, v1, length });
      vertices[v0].edges.push(e);
      if (v1 !== v0) vertices[v1].edges.push(e);
      start = i;
    }
  });
  return { vertices, edges };
}

/** Heading leaving vertex `v` along edge `e`, over its first ten metres or so. */
function headingFrom(draped: DrapedLine[], e: Edge, v: number): { x: number; z: number } {
  const d = draped[e.line];
  const forward = e.v0 === v;
  const first = forward ? e.i0 : e.i1;
  const step = forward ? 1 : -1;
  const x0 = d.pts[first * 2];
  const z0 = d.pts[first * 2 + 1];
  let i = first + step;
  let x1 = d.pts[i * 2];
  let z1 = d.pts[i * 2 + 1];
  while (Math.hypot(x1 - x0, z1 - z0) < 10 && i !== (forward ? e.i1 : e.i0)) {
    i += step;
    x1 = d.pts[i * 2];
    z1 = d.pts[i * 2 + 1];
  }
  const len = Math.hypot(x1 - x0, z1 - z0) || 1;
  return { x: (x1 - x0) / len, z: (z1 - z0) / len };
}

/**
 * Every journey a train can make: from each place a line leaves the region, along the
 * straightest way through every junction, to where it leaves again — or to a buffer stop,
 * where the train will wait and come back out.
 */
function findRuns(draped: DrapedLine[], network: { vertices: Vertex[]; edges: Edge[] }, stops: { x: number; z: number }[]): RunPath[] {
  const { vertices, edges } = network;
  const runs: RunPath[] = [];
  for (let start = 0; start < vertices.length; start++) {
    const sv = vertices[start];
    if (!sv.portal || sv.edges.length !== 1) continue;
    const used = new Set<number>();
    const walk: { edge: number; forward: boolean }[] = [];
    let v = start;
    let e = sv.edges[0];
    let ok = false;
    let terminus = false;
    for (let guard = 0; guard < 400; guard++) {
      used.add(e);
      const edge = edges[e];
      const forward = edge.v0 === v;
      walk.push({ edge: e, forward });
      const next = forward ? edge.v1 : edge.v0;
      const nv = vertices[next];
      if (nv.edges.length === 1) {
        if (nv.portal) ok = next !== start;
        else {
          terminus = true;
          ok = stops.some((s) => Math.hypot(s.x - nv.x, s.z - nv.z) < TERMINUS_STOP_REACH);
        }
        break;
      }
      // Arriving heading is the reverse of the heading leaving `next` back along this edge.
      const back = headingFrom(draped, edge, next);
      let best = -1;
      let bestTurn = Infinity;
      for (const candidate of nv.edges) {
        if (used.has(candidate)) continue;
        const out = headingFrom(draped, edges[candidate], next);
        // Straight on is `out` pointing exactly away from `back`.
        const turn = Math.acos(clamp(-(out.x * back.x + out.z * back.z), -1, 1));
        if (turn < bestTurn - 1e-9) {
          bestTurn = turn;
          best = candidate;
        }
      }
      if (best < 0 || bestTurn > MAX_DEFLECTION) break;
      v = next;
      e = best;
    }
    if (!ok) continue;
    const path = assemble(draped, edges, walk, terminus);
    if (path.length >= MIN_RUN) runs.push(path);
  }
  return runs;
}

function assemble(draped: DrapedLine[], edges: Edge[], walk: { edge: number; forward: boolean }[], terminus: boolean): RunPath {
  const xs: number[] = [];
  const zs: number[] = [];
  const ys: number[] = [];
  const hidden: number[] = [];
  const lineOf: number[] = [];
  const spans: { edge: number; s0: number; s1: number }[] = [];
  let s = 0;
  let electrified = 0;
  let total = 0;
  const cum: number[] = [];
  for (const step of walk) {
    const edge = edges[step.edge];
    const d = draped[edge.line];
    const from = step.forward ? edge.i0 : edge.i1;
    const to = step.forward ? edge.i1 : edge.i0;
    const dir = step.forward ? 1 : -1;
    const s0 = s;
    for (let i = from; ; i += dir) {
      if (xs.length && i === from) {
        // The shared vertex is already the last point of the previous edge.
      } else {
        if (xs.length) s += Math.hypot(d.pts[i * 2] - xs[xs.length - 1], d.pts[i * 2 + 1] - zs[zs.length - 1]);
        xs.push(d.pts[i * 2]);
        zs.push(d.pts[i * 2 + 1]);
        ys.push(d.top[i]);
        hidden.push(d.hidden ? 1 : 0);
        cum.push(s);
        lineOf.push(edge.line);
      }
      if (i === to) break;
    }
    spans.push({ edge: step.edge, s0, s1: s });
    total += edge.length;
    if (d.line.electrified) electrified += edge.length;
  }
  // `lineOf[i]` is the line of the segment from point i to i + 1.
  const segLine = new Int32Array(xs.length);
  for (let i = 0; i < xs.length; i++) segLine[i] = lineOf[Math.min(i + 1, xs.length - 1)];
  const first = draped[edges[walk[0].edge].line].line;
  return {
    xs: Float64Array.from(xs),
    zs: Float64Array.from(zs),
    ys: Float64Array.from(ys),
    s: Float64Array.from(cum),
    hidden: Uint8Array.from(hidden),
    lineOf: segLine,
    length: s,
    edges: spans,
    terminus,
    family: familyOf(first.kind),
    electrified: electrified > total / 2,
    key: `${first.id}:${walk.map((w) => (w.forward ? w.edge : -1 - w.edge)).join(',')}`,
  };
}

/** Where along a path a point is, if it is within `reach` of it. */
function projectOnPath(path: RunPath, x: number, z: number, reach: number, lines?: Set<number>): number | null {
  let best = reach * reach;
  let at: number | null = null;
  for (let i = 1; i < path.xs.length; i++) {
    if (lines && !lines.has(path.lineOf[i - 1])) continue;
    const ax = path.xs[i - 1];
    const az = path.zs[i - 1];
    const vx = path.xs[i] - ax;
    const vz = path.zs[i] - az;
    const len2 = vx * vx + vz * vz;
    if (len2 < 1e-9) continue;
    let t = ((x - ax) * vx + (z - az) * vz) / len2;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const d2 = (x - ax - vx * t) ** 2 + (z - az - vz * t) ** 2;
    if (d2 < best) {
      best = d2;
      at = path.s[i - 1] + t * (path.s[i] - path.s[i - 1]);
    }
  }
  return at;
}

// ---------------------------------------------------------------------------
// timetables
// ---------------------------------------------------------------------------

/** Constant acceleration from `t0`: `p = p0 + v0·τ + a·τ²/2`. */
interface Segment {
  t0: number;
  t1: number;
  p0: number;
  v0: number;
  a: number;
}

const ACCEL = 0.6;
const BRAKE = 0.8;
const STATION_DWELL = 25;
const TRAM_DWELL = 12;
const TERMINUS_DWELL = 30;
/** Ends this short of the buffer stop. */
const BUFFER_MARGIN = 4;

/**
 * Head position against time, for one trip along a path. `p` is the end of the train with
 * the larger path distance, so the train covers `[p − length, p]` whichever way it moves.
 */
function timetable(waypoints: { p: number; v: number; dwell: number }[], cruise: number): { segments: Segment[]; duration: number } {
  const segments: Segment[] = [];
  let t = 0;
  for (let k = 1; k < waypoints.length; k++) {
    const a = waypoints[k - 1];
    const b = waypoints[k];
    const distance = Math.abs(b.p - a.p);
    const dir = b.p >= a.p ? 1 : -1;
    if (distance > 1e-6) {
      const peak = Math.min(cruise, Math.sqrt((2 * ACCEL * BRAKE * distance + BRAKE * a.v * a.v + ACCEL * b.v * b.v) / (ACCEL + BRAKE)));
      let p = a.p;
      if (peak < Math.max(a.v, b.v) - 1e-6) {
        // Not room to do it gently: one constant rate from one speed to the other.
        const rate = (b.v * b.v - a.v * a.v) / (2 * distance);
        const dt = Math.abs(rate) > 1e-9 ? (b.v - a.v) / rate : distance / Math.max(a.v, 1e-3);
        segments.push({ t0: t, t1: t + dt, p0: p, v0: dir * a.v, a: dir * rate });
        t += dt;
      } else {
        const up = (peak * peak - a.v * a.v) / (2 * ACCEL);
        const down = (peak * peak - b.v * b.v) / (2 * BRAKE);
        const flat = Math.max(0, distance - up - down);
        if (up > 1e-6) {
          const dt = (peak - a.v) / ACCEL;
          segments.push({ t0: t, t1: t + dt, p0: p, v0: dir * a.v, a: dir * ACCEL });
          t += dt;
          p += dir * up;
        }
        if (flat > 1e-6) {
          const dt = flat / peak;
          segments.push({ t0: t, t1: t + dt, p0: p, v0: dir * peak, a: 0 });
          t += dt;
          p += dir * flat;
        }
        if (down > 1e-6) {
          const dt = (peak - b.v) / BRAKE;
          segments.push({ t0: t, t1: t + dt, p0: p, v0: dir * peak, a: -dir * BRAKE });
          t += dt;
        }
      }
    }
    if (b.dwell > 0) {
      segments.push({ t0: t, t1: t + b.dwell, p0: b.p, v0: 0, a: 0 });
      t += b.dwell;
    }
  }
  return { segments, duration: t };
}

/** The direction of the motion at `tau`, or of the next motion if the train is standing. */
function directionAt(segments: Segment[], tau: number): number {
  let i = 0;
  while (i < segments.length - 1 && segments[i].t1 <= tau) i++;
  for (let k = i; k < segments.length; k++) {
    const s = segments[k];
    const v = s.v0 + s.a * Math.max(0, Math.min(tau, s.t1) - s.t0);
    if (Math.abs(v) > 1e-3) return Math.sign(v);
    if (Math.abs(s.a) > 1e-9) return Math.sign(s.a);
  }
  return 1;
}

function evaluate(segments: Segment[], tau: number): { p: number; v: number } {
  let lo = 0;
  let hi = segments.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (segments[mid].t0 <= tau) lo = mid;
    else hi = mid - 1;
  }
  const s = segments[lo];
  const dt = Math.min(Math.max(0, tau - s.t0), s.t1 - s.t0);
  return { p: s.p0 + s.v0 * dt + 0.5 * s.a * dt * dt, v: s.v0 + s.a * dt };
}

/** Seconds between trains on any one run. Every run in a family shares it, which is what lets them be kept apart. */
const PERIOD: Record<Family, number> = { rail: 360, tram: 180, narrow: 600 };
/** Seconds either side of a train that no other train may be on the same track. */
const HEADWAY = 6;
/** A cap, so a big junction is busy rather than a conveyor belt. */
const MAX_RUNS: Record<Family, number> = { rail: 18, tram: 28, narrow: 4 };

interface Trip {
  run: number;
  path: RunPath;
  consist: Consist;
  segments: Segment[];
  duration: number;
  period: number;
  phase: number;
}

// ---------------------------------------------------------------------------
// crossings
// ---------------------------------------------------------------------------

/** Lights start this long before the train reaches the crossing. */
export const CROSSING_WARNING = 12;
/** And the booms start down this long after the lights. */
export const BARRIER_DELAY = 3;
/** Seconds a boom takes to come down, or go up. */
export const BARRIER_TRAVEL = 5;
/** Kept down this long after the last of the train has cleared. */
const CROSSING_CLEAR = 2;
/** Two closures closer together than this are one: the booms stay down for the second train. */
const CROSSING_MERGE_GAP = BARRIER_TRAVEL * 2 + 2;

export interface CrossingState {
  /** The red lights are flashing. */
  lights: boolean;
  /** 0 up, 1 down. */
  barrier: number;
  /** Seconds since the lights came on, for the flash phase. NaN when they are off. */
  since: number;
}

// ---------------------------------------------------------------------------
// what everyone asks of it
// ---------------------------------------------------------------------------

export interface TrainCarPose {
  type: RailCarType;
  livery: number;
  /** Body centre, at rail-head height. */
  x: number;
  y: number;
  z: number;
  /** Unit direction the car's own front faces. */
  dx: number;
  dz: number;
  /** Rise over run along `dx,dz`. */
  slope: number;
  /** Velocity, for the physics. */
  vx: number;
  vz: number;
}

export interface TrainPose {
  cars: TrainCarPose[];
  /**
   * Which way the train is going, as a unit vector at its head — or about to go, while it
   * stands at a platform. Null for a parked rake. What decides which lamps are white.
   */
  travel: { x: number; z: number } | null;
  /** Of `cars`, the one at the end the train is going towards. */
  lead: number;
  service: Service;
  /** Not running at all: a rake parked on a siding. */
  parked: boolean;
}

/** A car as the physics sees it: an oriented box in plan, and the height it stands at. */
export interface TrainBody {
  x: number;
  z: number;
  y: number;
  /** Unit vector along the car. */
  ux: number;
  uz: number;
  halfLength: number;
  halfWidth: number;
  height: number;
  vx: number;
  vz: number;
}

/**
 * The railway of one world, with its timetable. Build once per world; ask it about any time.
 */
export class RailTraffic {
  readonly draped: DrapedLine[];
  private readonly trips: Trip[] = [];
  private readonly parked: TrainPose[] = [];
  private readonly crossingEvents: { trip: number; a: number; b: number }[][] = [];
  readonly crossings: LevelCrossing[];

  private constructor(world: World, ground: RailGround) {
    this.draped = drapeRailLines(world, ground);
    this.crossings = world.rail?.crossings ?? [];
    if (!this.draped.length) return;
    const seed = world.seed;
    const network = buildNetwork(this.draped);
    const stops = world.rail?.stops ?? [];
    const runs = findRuns(this.draped, network, stops);
    this.schedule(world, runs, seed);
    this.timeCrossings();
    this.park(seed);
  }

  static build(world: World, ground: RailGround = railGroundFor(world)): RailTraffic {
    return new RailTraffic(world, ground);
  }

  /** How many trains run, for the tests and the debug overlay. */
  get tripCount(): number {
    return this.trips.length;
  }

  get isEmpty(): boolean {
    return this.trips.length === 0 && this.parked.length === 0;
  }

  /**
   * Every train in the region at `t` seconds of server time, parked rakes included. The last
   * answer is kept, because a frame asks twice — once to draw, once for the physics — and
   * both ask about the same moment. Callers must not change what comes back.
   */
  trainsAt(t: number): TrainPose[] {
    if (t === this.lastT) return this.lastTrains;
    const out = this.computeTrains(t);
    this.lastT = t;
    this.lastTrains = out;
    return out;
  }

  private lastT = NaN;
  private lastTrains: TrainPose[] = [];

  private computeTrains(t: number): TrainPose[] {
    const out: TrainPose[] = [];
    for (const trip of this.trips) {
      const tau0 = mod(t - trip.phase, trip.period);
      for (let k = 0; tau0 + k * trip.period < trip.duration; k++) {
        const pose = this.poseOf(trip, tau0 + k * trip.period);
        if (pose) out.push(pose);
      }
    }
    out.push(...this.parked);
    return out;
  }

  /** The same, as the boxes a car can hit. */
  bodiesAt(t: number): TrainBody[] {
    const out: TrainBody[] = [];
    for (const train of this.trainsAt(t)) {
      for (const car of train.cars) {
        const spec = RAIL_CAR_SPECS[car.type];
        out.push({
          x: car.x,
          z: car.z,
          y: car.y,
          ux: car.dx,
          uz: car.dz,
          halfLength: spec.length / 2,
          halfWidth: spec.width / 2,
          height: spec.height,
          vx: car.vx,
          vz: car.vz,
        });
      }
    }
    return out;
  }

  /** What crossing `index` (into `world.rail.crossings`) is doing at `t`. */
  crossingAt(index: number, t: number): CrossingState {
    const events = this.crossingEvents[index];
    const closed: CrossingState = { lights: false, barrier: 0, since: NaN };
    if (!events?.length) return closed;
    // Every closure near `t`, in absolute time, merged where they run into each other.
    const windows: [number, number][] = [];
    for (const e of events) {
      const trip = this.trips[e.trip];
      const tau0 = mod(t - trip.phase, trip.period);
      for (let k = -1; k <= Math.ceil(trip.duration / trip.period); k++) {
        const start = t - tau0 + k * trip.period;
        const open = start + e.a - CROSSING_WARNING;
        const shut = start + e.b + CROSSING_CLEAR;
        if (shut + BARRIER_TRAVEL + CROSSING_MERGE_GAP < t || open - CROSSING_MERGE_GAP > t) continue;
        windows.push([open, shut]);
      }
    }
    if (!windows.length) return closed;
    windows.sort((p, q) => p[0] - q[0]);
    const merged: [number, number][] = [];
    for (const w of windows) {
      const last = merged[merged.length - 1];
      if (last && w[0] <= last[1] + CROSSING_MERGE_GAP) last[1] = Math.max(last[1], w[1]);
      else merged.push([w[0], w[1]]);
    }
    for (const [open, shut] of merged) {
      if (t < open || t > shut + BARRIER_TRAVEL) continue;
      const down = smooth(clamp((t - open - BARRIER_DELAY) / BARRIER_TRAVEL, 0, 1));
      const up = t > shut ? smooth(clamp((t - shut) / BARRIER_TRAVEL, 0, 1)) : 0;
      // The lights run until the booms are back up, as they do in Poland.
      return { lights: true, barrier: Math.min(down, 1 - up), since: t - open };
    }
    return closed;
  }

  // -------------------------------------------------------------------------

  private schedule(world: World, runs: RunPath[], seed: number): void {
    const platforms = (world.rail?.platforms ?? []).map(platformShape);
    const stops = world.rail?.stops ?? [];
    const byFamily = new Map<Family, RunPath[]>();
    for (const run of runs) {
      const list = byFamily.get(run.family);
      if (list) list.push(run);
      else byFamily.set(run.family, [run]);
    }

    for (const family of ['rail', 'tram', 'narrow'] as const) {
      const list = (byFamily.get(family) ?? []).sort((a, b) => b.length - a.length || (a.key < b.key ? -1 : 1));
      const period = PERIOD[family];
      const busy = new Map<number, Uint8Array>();
      let taken = 0;
      for (const path of list) {
        if (taken >= MAX_RUNS[family]) break;
        const h = hashString(`${seed}|${path.key}`);
        const roll = (k: number) => hashUnit(h, k);
        const consist = consistFor(family, path.electrified, roll);
        const waypoints = this.waypointsFor(path, consist, platforms, stops);
        const { segments, duration } = timetable(waypoints, consist.speed);
        if (path.terminus && duration > period - HEADWAY * 2) continue;

        // Which edges this trip is on, second by second.
        const marks: { edge: number; slot: number }[] = [];
        const lastTau = Math.ceil(duration);
        for (let tau = 0; tau <= lastTau; tau++) {
          const { p } = evaluate(segments, tau);
          const lo = Math.max(0, p - consist.length);
          const hi = Math.min(path.length, p);
          if (hi <= lo) continue;
          for (const span of path.edges) {
            if (span.s1 <= lo || span.s0 >= hi) continue;
            marks.push({ edge: span.edge, slot: tau });
          }
        }

        let phase = -1;
        const first = Math.floor(roll(99) * period);
        for (let attempt = 0; attempt < period / 3; attempt++) {
          const candidate = (first + attempt * 3) % period;
          let clash = false;
          for (const m of marks) {
            const cells = busy.get(m.edge);
            if (cells && cells[(m.slot + candidate) % period]) {
              clash = true;
              break;
            }
          }
          if (!clash) {
            phase = candidate;
            break;
          }
        }
        if (phase < 0) continue;
        for (const m of marks) {
          let cells = busy.get(m.edge);
          if (!cells) {
            cells = new Uint8Array(period);
            busy.set(m.edge, cells);
          }
          for (let d = -HEADWAY; d <= HEADWAY; d++) cells[mod(m.slot + phase + d, period)] = 1;
        }
        this.trips.push({ run: this.trips.length, path, consist, segments, duration, period, phase });
        taken++;
      }
    }
  }

  private waypointsFor(
    path: RunPath,
    consist: Consist,
    platforms: PlatformShape[],
    stops: { x: number; z: number; kind: string }[],
  ): { p: number; v: number; dwell: number }[] {
    const L = consist.length;
    const calls: number[] = [];
    if (consist.stops) {
      const centres: number[] = [];
      if (consist.service !== 'tram') {
        for (const pl of platforms) {
          const at = projectOnPath(path, pl.x, pl.z, pl.halfWidth + 3.5);
          if (at !== null) centres.push(at);
        }
      }
      for (const stop of stops) {
        const tram = stop.kind === 'tram_stop';
        if (tram !== (consist.service === 'tram')) continue;
        const at = projectOnPath(path, stop.x, stop.z, tram ? 15 : 30);
        if (at === null) continue;
        // A station node with a platform beside it is the same call.
        if (centres.some((c) => Math.abs(c - at) < 150)) continue;
        centres.push(at);
      }
      centres.sort((a, b) => a - b);
      for (const c of centres) {
        const p = c + L / 2;
        if (p < L + 20 || p > path.length - 10) continue;
        if (calls.length && p - calls[calls.length - 1] < 120) continue;
        calls.push(p);
      }
    }
    const dwell = consist.service === 'tram' ? TRAM_DWELL : STATION_DWELL;
    const v = consist.speed;
    const points: { p: number; v: number; dwell: number }[] = [{ p: 0, v, dwell: 0 }];
    if (path.terminus) {
      const end = path.length - BUFFER_MARGIN;
      for (const c of calls) if (c < end - 60) points.push({ p: c, v: 0, dwell });
      points.push({ p: end, v: 0, dwell: TERMINUS_DWELL });
      for (const c of [...calls].reverse()) if (c < end - 60) points.push({ p: c, v: 0, dwell });
      points.push({ p: 0, v, dwell: 0 });
    } else {
      for (const c of calls) points.push({ p: c, v: 0, dwell });
      points.push({ p: path.length + L, v, dwell: 0 });
    }
    return points;
  }

  private poseOf(trip: Trip, tau: number): TrainPose | null {
    const { p, v } = evaluate(trip.segments, tau);
    const path = trip.path;
    const cars: TrainCarPose[] = [];
    let alongX = 0;
    let alongZ = 0;
    for (const car of trip.consist.cars) {
      const spec = RAIL_CAR_SPECS[car.type];
      const front = p - car.offset;
      const back = front - spec.length;
      if (back < 0 || front > path.length) continue;
      const inset = spec.length * 0.17;
      const a = pointOn(path, front - inset);
      const b = pointOn(path, back + inset);
      if (a.hidden || b.hidden) continue;
      const dx = a.x - b.x;
      const dz = a.z - b.z;
      const len = Math.hypot(dx, dz) || 1;
      const ux = dx / len;
      const uz = dz / len;
      if (!cars.length) {
        alongX = ux;
        alongZ = uz;
      }
      cars.push({
        type: car.type,
        livery: car.livery,
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        z: (a.z + b.z) / 2,
        dx: car.flip ? -ux : ux,
        dz: car.flip ? -uz : uz,
        slope: ((a.y - b.y) / len) * (car.flip ? -1 : 1),
        vx: ux * v,
        vz: uz * v,
      });
    }
    if (!cars.length) return null;
    const dir = directionAt(trip.segments, tau);
    return {
      cars,
      // Along the path at the head end, not the first car's own front, which may face back.
      travel: { x: alongX * dir, z: alongZ * dir },
      lead: dir > 0 ? 0 : cars.length - 1,
      service: trip.consist.service,
      parked: false,
    };
  }

  private timeCrossings(): void {
    this.crossings.forEach((crossing, index) => {
      const events: { trip: number; a: number; b: number }[] = [];
      const lineIndices = new Set<number>();
      this.draped.forEach((d, li) => {
        if (crossing.lines.includes(d.line.id)) lineIndices.add(li);
      });
      this.trips.forEach((trip, ti) => {
        const at = projectOnPath(trip.path, crossing.x, crossing.z, crossing.half + 8, lineIndices);
        if (at === null) return;
        const L = trip.consist.length;
        // When any of the train is within a couple of metres of the crossing.
        let a = NaN;
        let b = NaN;
        for (let tau = 0; tau <= trip.duration; tau += 0.25) {
          const { p } = evaluate(trip.segments, tau);
          const on = p - L <= at + 2 && p >= at - 2;
          if (on) {
            if (Number.isNaN(a)) a = tau;
            b = tau;
          } else if (!Number.isNaN(a)) {
            events.push({ trip: ti, a, b });
            a = NaN;
          }
        }
        if (!Number.isNaN(a)) events.push({ trip: ti, a, b });
      });
      this.crossingEvents[index] = events;
    });
  }

  /**
   * Wagons standing in the yards. A siding with nothing on it looks like a drawing of one;
   * a third of them get a rake, chosen by hash, and it never moves.
   */
  private park(seed: number): void {
    for (const d of this.draped) {
      if (!d.line.siding || d.hidden || familyOf(d.line.kind) !== 'rail') continue;
      const roll = (k: number) => hashUnit(hashString(`${seed}|park|${d.line.id}`), k);
      if (roll(0) > 0.35) continue;
      const path = singlePath(d);
      if (path.length < 50) continue;
      const kinds: RailCarType[] = ['wagon-box', 'wagon-tank', 'wagon-hopper', 'wagon-flat', 'coach'];
      const kind = kinds[Math.floor(roll(1) * kinds.length)];
      const spec = RAIL_CAR_SPECS[kind];
      const room = path.length - 20;
      const count = Math.min(Math.floor(room / (spec.length + COUPLED_GAP)), 2 + Math.floor(roll(2) * 10));
      if (count < 1) continue;
      const length = count * (spec.length + COUPLED_GAP) - COUPLED_GAP;
      const head = 10 + roll(3) * (room - length) + length;
      const cars: TrainCarPose[] = [];
      for (let i = 0; i < count; i++) {
        const front = head - i * (spec.length + COUPLED_GAP);
        const a = pointOn(path, front - spec.length * 0.17);
        const b = pointOn(path, front - spec.length * 0.83);
        const len = Math.hypot(a.x - b.x, a.z - b.z) || 1;
        cars.push({
          type: kind,
          livery: Math.floor(roll(10 + i) * LIVERY_COUNT[kind]),
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
          z: (a.z + b.z) / 2,
          dx: (a.x - b.x) / len,
          dz: (a.z - b.z) / len,
          slope: (a.y - b.y) / len,
          vx: 0,
          vz: 0,
        });
      }
      this.parked.push({ cars, travel: null, lead: 0, service: 'freight', parked: true });
    }
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

interface PlatformShape {
  x: number;
  z: number;
  halfWidth: number;
}

/** A platform's middle and roughly how wide it is: its area over its length, for an outline. */
function platformShape(pl: { pts: number[]; closed: boolean }): PlatformShape {
  const n = pl.pts.length / 2;
  let x = 0;
  let z = 0;
  for (let i = 0; i < n; i++) {
    x += pl.pts[i * 2];
    z += pl.pts[i * 2 + 1];
  }
  x /= n;
  z /= n;
  if (!pl.closed) return { x, z, halfWidth: 3 };
  let area = 0;
  let longest = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pl.pts[i * 2] * pl.pts[j * 2 + 1] - pl.pts[j * 2] * pl.pts[i * 2 + 1];
    longest = Math.max(longest, Math.hypot(pl.pts[j * 2] - pl.pts[i * 2], pl.pts[j * 2 + 1] - pl.pts[i * 2 + 1]));
  }
  return { x, z, halfWidth: Math.max(1.5, Math.abs(area / 2) / Math.max(longest, 1) / 2) };
}

function singlePath(d: DrapedLine): RunPath {
  const n = d.pts.length / 2;
  const xs = new Float64Array(n);
  const zs = new Float64Array(n);
  const s = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    xs[i] = d.pts[i * 2];
    zs[i] = d.pts[i * 2 + 1];
    if (i) s[i] = s[i - 1] + Math.hypot(xs[i] - xs[i - 1], zs[i] - zs[i - 1]);
  }
  return {
    xs,
    zs,
    ys: Float64Array.from(d.top),
    s,
    hidden: new Uint8Array(n),
    lineOf: new Int32Array(n),
    length: s[n - 1] ?? 0,
    edges: [],
    terminus: false,
    family: familyOf(d.line.kind),
    electrified: !!d.line.electrified,
    key: `${d.line.id}`,
  };
}

function pointOn(path: RunPath, s: number): { x: number; y: number; z: number; hidden: boolean } {
  const n = path.xs.length;
  if (s <= 0) return { x: path.xs[0], y: path.ys[0], z: path.zs[0], hidden: !!path.hidden[0] };
  if (s >= path.length) return { x: path.xs[n - 1], y: path.ys[n - 1], z: path.zs[n - 1], hidden: !!path.hidden[n - 1] };
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (path.s[mid] <= s) lo = mid;
    else hi = mid;
  }
  const span = path.s[hi] - path.s[lo];
  const t = span > 1e-9 ? (s - path.s[lo]) / span : 0;
  return {
    x: lerp(path.xs[lo], path.xs[hi], t),
    y: lerp(path.ys[lo], path.ys[hi], t),
    z: lerp(path.zs[lo], path.zs[hi], t),
    hidden: !!(path.hidden[lo] && path.hidden[hi]),
  };
}

function mod(a: number, m: number): number {
  return ((a % m) + m) % m;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

function smooth(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}
